import parsePhoneNumber from 'libphonenumber-js';
import { isRole } from './tsHelper';
import { NewDataReturn, FindTelecom, currentLocationPos } from '../../interfaces/interfaces';
import { sortDaysOfWeek } from '../sort/sort';

const findFnHandlers = (value: fhir4.PractitionerRole | fhir4.Location, item: NewDataReturn, key: string) => {
  const isTrue = isRole(value);

  let practitionerRole: fhir4.PractitionerRole;
  let location: fhir4.Location | undefined;
  if (isTrue) {
    practitionerRole = value as fhir4.PractitionerRole;
    const locationId = practitionerRole.location?.at(0)?.reference?.split('/').at(1);
    if (locationId) location = item.location.get(locationId);
  } else {
    location = value as fhir4.Location;
  }

  let practitioner: fhir4.Practitioner | undefined;
  if (isTrue) {
    const practitionerId = (value as fhir4.PractitionerRole).practitioner?.reference?.split('/').at(1);
    if (practitionerId) practitioner = item.practitioner.get(practitionerId);
  }

  let organization: fhir4.Organization | undefined;
  if (!isTrue) {
    const organizationId = (value as fhir4.Location).managingOrganization?.reference?.split('/').at(1);
    if (organizationId) organization = item.organization.get(organizationId);
  }

  const removeEmptyString = (text: string | undefined) =>
    text
      ?.split(',')
      .map((i) => i.trim())
      .filter(Boolean)
      .join(', ') ?? '';

  const findId = () => value?.id;
  const findName = () => (isTrue ? practitioner?.name?.[0]?.text : value?.name);

  const findOrgType = () => (isTrue ? null : value?.type?.[0]?.text);
  const findPharmacyType = () => (isTrue ? null : location?.description);
  const findPharmacyChain = () => (isTrue ? null : organization?.name);
  const findRole = () => (isTrue ? practitionerRole : null);

  const findOrganizationAddress = (addressArr: fhir4.Address | undefined) => {
    const { text = '', line = [], city = '', state = '' } = addressArr ?? {};

    const address = removeEmptyString(text);

    const addressShort = removeEmptyString(`${removeEmptyString(line?.[0])}, ${removeEmptyString(city)}, ${removeEmptyString(state)}`);

    return { address, addressShort };
  };

  const findValueLat = () => {
    return location?.position?.latitude ?? 56;
  };

  const findValueLng = () => {
    return location?.position?.longitude ?? -112;
  };

  const findAddress = () => {
    const address = location?.address;
    return findOrganizationAddress(address).address;
  };

  const findLocationId = () => {
    return location?.id;
  };

  const findAddressShort = () => {
    const address = location?.address;
    return findOrganizationAddress(address).addressShort;
  };

  const findLocationSecondary = () => {
    if (isTrue) {
      return (practitioner?.extension ?? []).filter((extension) => extension.url === 'other_role').map((extension) => extension.valueString);
    }
    return null;
  };

  const findGeneralSpecialty = () => {
    if (isTrue) {
      const specialty: (string | undefined)[] = [];
      const generalSpecialty = practitionerRole?.specialty?.find((n) => n?.text);
      specialty.push(generalSpecialty?.text, '-');

      return specialty.filter(Boolean).at(0);
    }
    return null;
  };

  const findSecondarySpecialties = () => {
    if (isTrue) {
      const specialties = new Set<string>();

      practitionerRole?.specialty?.forEach((specialty) => {
        if (specialty?.text) {
          specialties.add(specialty.text);
        }
      });

      return [...specialties].slice(1);
    }

    return null;
  };

  const findTelecom: FindTelecom = (valueOfTelecom) => (i) => i?.system === valueOfTelecom;
  const contactType = (fn: FindTelecom) => (name: string) => {
    let telecom: string | undefined;
    if (isTrue) {
      telecom = practitionerRole?.telecom?.find(fn(name))?.value;
    } else {
      telecom = location?.telecom?.find(fn(name))?.value;
    }

    if (telecom && (name === 'phone' || name === 'fax')) {
      return parsePhoneNumber(telecom, 'CA')?.formatNational();
    }
    return telecom;
  };
  const findContactType = contactType(findTelecom);

  const findRegistrationProvince = () => {
    if (isTrue) {
      return practitioner?.identifier?.filter((identifier) => {
        return identifier.type?.coding?.some((coding) => coding.code === 'MD');
      });
    }
    return null;
  };

  const findBillingNumbers = () => {
    if (isTrue) {
      return practitioner?.identifier?.filter((identifier) => {
        return identifier.type?.coding?.some((coding) => coding.code === 'BILL');
      });
    }
    return null;
  };

  const findCoding = () => (isTrue ? null : location?.type?.[0]?.coding?.[0]?.display);
  const findGender = () => (isTrue ? practitioner?.gender : null);

  const findLanguage = () => {
    if (isTrue) {
      return practitioner?.communication
        ?.map((i) => i.text)
        ?.filter(Boolean)
        ?.join(', ');
    }
    return null;
  };

  const findContactPerson = () => {
    return null;
  };

  const findCatchmentArea = () => {
    if (isTrue) {
      return location?.address?.district;
    }
    return null;
  };

  const findDistanceToOrigin = (currentLocation: currentLocationPos | null) => {
    if (currentLocation == null) {
      return 0;
    }

    const position = {
      lat: findValueLat() === 56 ? currentLocation.lat : findValueLat() ?? currentLocation.lat,
      lng: findValueLng() === -112 ? currentLocation.lng : findValueLng() ?? currentLocation.lat,
    };
    return Number((window.google.maps.geometry.spherical.computeDistanceBetween(currentLocation, position) / 1000).toFixed(1));
  };
  const findOperationHours = () => sortDaysOfWeek(item.location?.get(key)?.hoursOfOperation);

  return {
    findId,
    findName,
    findOrgType,
    findRole,
    findGeneralSpecialty,
    findSecondarySpecialties,
    findCoding,
    findLanguage,
    findPharmacyType,
    findPharmacyChain,
    findAddress,
    findGender,
    findLocationSecondary,
    findContactType,
    findRegistrationProvince,
    findBillingNumbers,
    findContactPerson,
    findCatchmentArea,
    findValueLat,
    findValueLng,
    findAddressShort,
    findDistanceToOrigin,
    findOperationHours,
    findLocationId,
  };
};

export default findFnHandlers;
